/**
 * @author: lv.chen
 * @date: 2025/8/7
 * @desc: MarketReportForm组件
 */
import React, { useRef, useMemo, useState, useImperativeHandle } from 'react';
import classnames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { history, useIntl } from 'umi';
import { useDebounceFn } from 'ahooks';
import { useForm, useField } from 'react-final-form-hooks';
import { Button, Modal, Form, Input, TextArea, Switch } from 'tea-component';
import CardSlotV2 from '@intelligence/components/CardSlotV2';
import FileNameCard from '@user-center/components/FileNameCard';
import ReportUploader from '../../../components/ReportUploader';
import LocationSelect from '../../../components/LocationSelect';
import PermissionSelect from '../../../components/PermissionSelect';
import ImageUploader from '../../../components/ImageUploader';
import ReportPreviewModal from '../../../components/ReportPreviewModal';
import ReportRadio from '../../../components/ReportRadio';
import useCOS, { getUseAccelerateByENV } from '@gp/hooks/useCos/useCOS';
import { recognizeReportService } from '@home/services/report';
import { normalizeLanguage } from '@intelligence/utils/reportLanguage';
import {
  fetchUserInformation,
  fetchReportGZTencentQCouldMassage,
  fetchReportSGTencentQCouldMassage,
  uploadHTMLZipReport,
  addReportAuthPaths,
} from '../../../services';
import { isEmpty, isNotEmpty } from '@intelligence/v3/utils/common';
import { getIsHTMLReportPath, getIsZipReportPath, parseReportOriginalFilePaths } from '@home/utils/reportPreview';
import { formatTeaFormItemStatus, getNullableURL } from '@user-center/utils';
import {
  formatPermissions,
  getIsAvailable,
  getDefaultReportCoverFileName,
  getFileNameFromPath,
  getImageFileDataCrossOriginPossibility,
  getLocationOptionsWithDisabled,
} from '../../../utils';
import { buildMarketReport, getMarketReportLocationValue, handleMarketReportValidate } from '../../utils';
import { DBI_FILE_COS_PATH, DBI_IMG_COS_PATH } from '@user-center/_config';
import { LOCATION_OPTIONS, DEFAULT_FORM_MODEL, REPORT_FILE_LANGUAGE_OPTIONS } from '../../constants';

import type { CascaderOption } from 'tea-component';

import '../../../components/ReportForm/index.scss';

import IMG_AI_TEXT from '@home/assets/img/ai-text.svg';

const iconList: any[] = [];

const showStatusIcon = false;

const getFileLanguageValue = (language?: string): string => {
  const normalizedLanguage = normalizeLanguage(language || '');
  if (REPORT_FILE_LANGUAGE_OPTIONS.includes(normalizedLanguage)) {
    return normalizedLanguage;
  }
  return '';
};

export interface IReportFormInstance {
  preview: (report?: IReport) => void;
  initialize: (report: IReport) => void;
  initializeWhenRecognizedReport: (file: File, result: any) => void;
  updateCreatorInformation: (openId: string) => void;
}

interface IReportFormProps extends IBaseProps {
  reportPublishLoading: boolean;
  permissionOptions: CascaderOption[];
  clearOnRecognitionError?: boolean;
  onConfirm?: (values: IReportPartial) => Promise<boolean> | boolean;
  onBack?: () => void;
  onPreview?: () => void;
  onPublish?: (values: IReportPartial) => void;
  onUploadCover?: (file: File) => void;
}

const buildReportFileData = (path: string, originalPath = ''): IReportFileData | null => {
  if (!path) return null;

  return {
    key: path,
    originalKey: originalPath,
    url: '',
    file_name: getFileNameFromPath(originalPath || path),
    file: null,
  };
};

const MarketReportForm: React.ForwardRefRenderFunction<IReportFormInstance, IReportFormProps> = (props, ref) => {
  const {
    style,
    className,
    reportPublishLoading,
    permissionOptions,
    clearOnRecognitionError = false,
    onConfirm,
    onBack,
    onPreview,
    onPublish,
    onUploadCover,
  } = props;

  // 仅预发布、正式 支持COS全球域名加速请求
  const useAccelerate = getUseAccelerateByENV();

  const { formatMessage } = useIntl();

  const { putObject } = useCOS({
    useAccelerate,
    baseCOSConfigFetcher: fetchReportGZTencentQCouldMassage,
    authorizationFetcher: fetchReportGZTencentQCouldMassage,
  });

  const { getObjectUrl: getObjectUrlSG, putObject: putObjectSG } = useCOS({
    useAccelerate,
    baseCOSConfigFetcher: fetchReportSGTencentQCouldMassage,
    authorizationFetcher: fetchReportSGTencentQCouldMassage,
  });

  const filteredLocationOptions = LOCATION_OPTIONS;

  const validate = (values: IReportFormModel) => {
    return handleMarketReportValidate(values);
  };

  const { form, values, submitting, handleSubmit } = useForm({
    initialValues: DEFAULT_FORM_MODEL,
    validate,
    onSubmit: async (values: IReportFormModel) => {
      try {
        const [fileNew, uploadedImageValues] = await Promise.all([
          uploadMarketReportFile(values),
          uploadCoverAndCreatorAvatarByValues(values),
        ]);
        const valuesNew = {
          ...uploadedImageValues,
          file: fileNew,
          file_en: null,
        };

        form.batch(() => {
          form.change('file', fileNew);
          form.change('file_en', null);
          form.change('cover', uploadedImageValues.cover);
          form.change('creator_avatar', uploadedImageValues.creator_avatar);
        });

        const report = buildMarketReport(valuesNew, {
          permissionOptions,
          reportHelperInfo: reportHelperInfo.current,
        });
        const confirmSuccess = await onConfirm?.(report);
        if (confirmSuccess === false) {
          setSubmitSuccess(false);
          return;
        }
        setSubmitSuccess(true);
        setLocationRestrictionEnabled(true);
      } catch (error) {
        console.error(error);
        setSubmitSuccess(false);
      } finally {
        setSubmitted(false);
      }
    },
  });

  const fileField = useField('file', form);
  const fileLanguageField = useField('file_language', form);
  const titleField = useField('title', form);
  const titleENField = useField('title_en', form);
  const introductionField = useField('introduction', form);
  const introductionENField = useField('introduction_en', form);
  const tagField = useField('tag', form);
  const tagENField = useField('tag_en', form);
  const coverField = useField('cover', form);
  const locationField = useField('location', form);
  const permissionField = useField('permission', form);
  const downloadEnableField = useField('download_enable', form);
  const watermarkEnableField = useField('watermark_enable', form);

  const file = values?.file;
  const fileName = file?.file_name || '';

  const cover = values?.cover;
  const coverUrl = cover?.url || '';

  /**
   * 报告的其他信息（报告AI读取到，根据open_id自动拉取到的，但是页面表单又不展示，不提供修改入口的信息，新增编辑报告需要用到）
   */
  const reportHelperInfo = useRef({
    report_language: '',
    report_language_en: '',
    file_cn_language: '',
    file_en_language: '',
    creator_name: '',
    creator_department: '',
  });

  const formAvailable = useMemo<boolean>(() => getIsAvailable(values), [values]);
  const formValid = useMemo<boolean>(() => isEmpty(handleMarketReportValidate(values)), [values]);

  const [submitSuccess, setSubmitSuccess] = useState(false);

  /**
   * 是否启用 Location 选择限制
   * - Edit 模式：初始化后启用
   * - Add 模式：保存成功后启用
   */
  const [locationRestrictionEnabled, setLocationRestrictionEnabled] = useState(false);

  /**
   * 计算带 disabled 字段的 Location options
   * 禁用规则：
   * 1. 当前选中 Game Research Report 时，禁用其他所有一级分类
   * 2. 当前选中非 Game Research Report 时，禁用 Game Research Report
   */
  const locationOptionsWithDisabled = useMemo(() => {
    if (!locationRestrictionEnabled) {
      return filteredLocationOptions;
    }
    const currentL1Value = values?.location?.[0];
    return getLocationOptionsWithDisabled(filteredLocationOptions, currentL1Value);
  }, [values?.location, filteredLocationOptions, locationRestrictionEnabled]);

  const [submitted, setSubmitted] = useState(false);

  const [coverLoading, setCoverLoading] = useState(false);

  const [previewLoading, setPreviewLoading] = useState(false);

  const [previewVisible, setPreviewVisible] = useState(false);

  const [previewReport, setPreviewReport] = useState<IReportPartial | null>(null);

  const handleSubmitInPage = () => {
    setSubmitted(true);
    handleSubmit();
  };

  const updateCoverSilent = async (params: { fileName: string; cover: string; timestamp: number }) => {
    const { fileName, cover, timestamp } = params || {};

    try {
      setCoverLoading(true);

      const coverFileNameInitial = getDefaultReportCoverFileName(fileName);

      const {
        fileKey: coverKey,
        fileName: coverFileName,
        fileUrl: coverUrl,
        file: coverFile,
      } = await getImageFileDataCrossOriginPossibility({
        url: cover,
        getFileKey: () => `${DBI_IMG_COS_PATH}/${timestamp}_${coverFileNameInitial}`,
        getFileName: () => coverFileNameInitial,
      });

      form.batch(() => {
        form.change('cover', {
          key: coverKey,
          url: coverUrl,
          file_name: coverFileName,
          file: coverFile,
        });
      });
    } catch (error) {
      console.error(error);
    } finally {
      setCoverLoading(false);
    }
  };

  const handleRecognize = (file: File, result: any) => {
    const fileName = file?.name || '';
    const recognizedReportData = result?.data || {};
    const language = recognizedReportData?.report_language || '';
    const languageEN = recognizedReportData?.report_language_en || '';
    const detectedLanguage = getFileLanguageValue(languageEN || language);
    const timestamp = Date.now();

    reportHelperInfo.current.file_cn_language = detectedLanguage;
    reportHelperInfo.current.file_en_language = detectedLanguage;
    reportHelperInfo.current.report_language = language;
    reportHelperInfo.current.report_language_en = languageEN;

    form.batch(() => {
      form.change('file', {
        key: `${DBI_FILE_COS_PATH}/${timestamp}_${fileName}`,
        url: '',
        file_name: fileName,
        file,
      });
      form.change('file_language', detectedLanguage);
      if (!!recognizedReportData?.title) {
        form.change('title', recognizedReportData.title);
      }
      if (!!recognizedReportData?.title_en) {
        form.change('title_en', recognizedReportData.title_en);
      }
      if (!!recognizedReportData?.introduction) {
        form.change('introduction', recognizedReportData.introduction);
      }
      if (!!recognizedReportData?.introduction_en) {
        form.change('introduction_en', recognizedReportData.introduction_en);
      }
      if (!!recognizedReportData?.tag?.length) {
        form.change('tag', recognizedReportData.tag.join(' | '));
      }
      if (!!recognizedReportData?.tag_en?.length) {
        form.change('tag_en', recognizedReportData.tag_en.join(' | '));
      }
    });

    if (!!recognizedReportData?.cover) {
      form.change('cover', null);
      updateCoverSilent({ fileName, cover: recognizedReportData.cover, timestamp });
    }
  };

  const updateCreatorAvatarSilent = async (creatorUserName: string, avatar: string) => {
    try {
      const creatorAvatarFileNameInitial = `${creatorUserName}.jpeg`;

      const {
        fileKey: creatorAvatarKey,
        fileName: creatorAvatarFileName,
        fileUrl: creatorAvatarUrl,
        file: creatorAvatarFile,
      } = await getImageFileDataCrossOriginPossibility({
        url: avatar,
        getFileKey: () => `${DBI_IMG_COS_PATH}/report-avatar-${creatorAvatarFileNameInitial}`,
        getFileName: () => creatorAvatarFileNameInitial,
      });

      form.batch(() => {
        form.change('creator_avatar', {
          key: creatorAvatarKey,
          url: creatorAvatarUrl,
          file_name: creatorAvatarFileName,
          file: creatorAvatarFile,
        });
      });
    } catch (error) {
      console.error(error);
    }
  };

  const updateCreatorInformation = async (creatorUserName: string) => {
    const res = await fetchUserInformation({ username: creatorUserName });
    const resData = res?.data;
    const userFullName = resData?.full_name || '';
    const userName = resData?.user_name || '';
    const avatar = resData?.user_pic || '';
    const departmentName = resData?.department_name || '';

    reportHelperInfo.current.creator_name = userFullName || userName || creatorUserName;
    reportHelperInfo.current.creator_department = departmentName;

    form.batch(() => {
      form.change('creator_avatar', null);

      form.change('creator_department_en', departmentName);
    });

    updateCreatorAvatarSilent(creatorUserName, avatar);
  };

  const { run: updateCreatorInformationDebounce } = useDebounceFn(updateCreatorInformation, { wait: 600 });

  const uploadReportFile = async (fileData?: IReportFileData | null): Promise<IReportFileData | null> => {
    if (!fileData) {
      return null;
    }
    if (!fileData?.key || !fileData?.file) {
      return { ...fileData };
    }

    const reportFile = fileData.file;
    const isHTMLZipReport = getIsHTMLReportPath(reportFile.name) || getIsZipReportPath(reportFile.name);
    if (!isHTMLZipReport) {
      await putObject({ key: fileData.key, file: reportFile });
      return { ...fileData, originalKey: '', file: null };
    }

    const reportId = `g-${uuidv4()}`;
    const [response] = await Promise.all([
      uploadHTMLZipReport({ file: reportFile, key: reportId }),
      putObject({ key: fileData.key, file: reportFile }),
    ]);
    const htmlPath = response?.data?.htmlPath || '';
    if (!htmlPath) {
      throw new Error('HTML/ZIP report upload did not return htmlPath.');
    }
    const htmlFiles = response?.data?.htmlFiles || [];
    const paths = Array.from(new Set([htmlPath, ...htmlFiles].filter(Boolean)));
    await addReportAuthPaths({
      report_id: reportId,
      paths,
    });
    return {
      ...fileData,
      key: htmlPath,
      originalKey: fileData.key,
      file: null,
    };
  };

  const uploadMarketReportFile = async (reportValues: IReportFormModel): Promise<IReportFileData | null> => {
    return uploadReportFile(reportValues?.file);
  };

  const uploadImageFileData = async (fileData?: IReportFileData | null): Promise<IReportFileData | null> => {
    if (!fileData) {
      return null;
    }

    if (!fileData?.key || !fileData?.file) {
      return { ...fileData, file: null };
    }

    await putObjectSG({ key: fileData.key, file: fileData.file });
    const urlMap = await getObjectUrlSG({ key: fileData.key, sign: false });
    return { ...fileData, url: urlMap?.url || '', file: null };
  };

  const uploadCoverAndCreatorAvatarByValues = async (reportValues: IReportFormModel): Promise<IReportFormModel> => {
    const [coverNew, creatorAvatarNew] = await Promise.all([
      uploadImageFileData(reportValues?.cover),
      uploadImageFileData(reportValues?.creator_avatar),
    ]);

    return { ...reportValues, cover: coverNew, creator_avatar: creatorAvatarNew };
  };

  const handlePublish = async () => {
    try {
      const valuesNew = await uploadCoverAndCreatorAvatarByValues(values);

      form.batch(() => {
        form.change('cover', valuesNew.cover);
        form.change('creator_avatar', valuesNew.creator_avatar);
      });

      const report = buildMarketReport(valuesNew, { permissionOptions, reportHelperInfo: reportHelperInfo.current });
      onPublish?.(report);
      setSubmitted(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handlePublishAfterValidate = async () => {
    setSubmitted(true);
    const errors = validate(values);
    Object.keys(errors).forEach((fieldName) => {
      form.blur(fieldName);
    });

    if (isNotEmpty(errors)) {
      return false;
    }

    await handlePublish();
    return true;
  };

  const reset = () => {
    reportHelperInfo.current.report_language = '';
    reportHelperInfo.current.report_language_en = '';
    reportHelperInfo.current.file_cn_language = '';
    reportHelperInfo.current.file_en_language = '';

    form.restart({
      ...DEFAULT_FORM_MODEL,
      creator_name_en: values?.creator_name_en || '',
      creator_avatar: values?.creator_avatar || null,
      creator_department_en: values?.creator_department_en || '',
    });

    setLocationRestrictionEnabled(false);
    setSubmitSuccess(false);
    setSubmitted(false);
  };

  const handleRecognitionError = () => {
    if (clearOnRecognitionError) {
      reset();
    }
  };

  useImperativeHandle(ref, () => ({
    preview: (report) => {
      setPreviewVisible(true);
      setPreviewReport({
        ...report,
        ...buildMarketReport(values, { permissionOptions, reportHelperInfo: reportHelperInfo.current }),
      });
    },
    initialize: (report) => {
      const system = report?.system || '';
      const source = report?.source || '';
      const fileName = report?.file_name || '';
      const fileENName = report?.file_name_en || '';
      const { filePathCN: originalFilePath, filePathEN: originalFileENPath } = parseReportOriginalFilePaths(
        report?.original_file_name,
      );
      const coverURL = getNullableURL(report?.cover);
      const creatorAvatarURL = getNullableURL(report?.creator_head_picture);
      const coverKey = decodeURI(coverURL?.pathname || report?.cover || '');
      const creatorAvatarKey = decodeURI(creatorAvatarURL?.pathname || report?.creator_head_picture || '');

      const fileLanguage = getFileLanguageValue(
        report?.file_cn_language || report?.file_en_language || report?.report_language_en || report?.report_language,
      );
      const marketReportFileName = fileName || fileENName;
      const marketReportOriginalFilePath = originalFilePath || originalFileENPath;
      reportHelperInfo.current.file_cn_language = report?.file_cn_language || '';
      reportHelperInfo.current.file_en_language = report?.file_en_language || '';
      reportHelperInfo.current.report_language = report?.report_language || '';
      reportHelperInfo.current.report_language_en = report?.report_language_en || '';
      reportHelperInfo.current.creator_name = report?.creator_name || '';
      reportHelperInfo.current.creator_department = report?.creator_department || '';

      const reportFormModel: IReportFormModel = {
        file: buildReportFileData(marketReportFileName, marketReportOriginalFilePath),
        file_en: null,
        file_language: fileLanguage,
        title: report?.title || '',
        title_en: report?.title_en || '',
        introduction: report?.introduction || '',
        introduction_en: report?.introduction_en || '',
        tag: report?.tag || '',
        tag_en: report?.tag_en || '',
        cover: {
          key: coverKey,
          url: coverURL?.href || '',
          file_name: getFileNameFromPath(coverKey),
          file: null,
        },
        location: getMarketReportLocationValue(system, source),
        permission: formatPermissions(report?.permission, permissionOptions),
        creator_name_en: report?.creator_name_en || '',
        creator_avatar: {
          key: creatorAvatarKey,
          url: creatorAvatarURL?.href || '',
          file_name: getFileNameFromPath(creatorAvatarKey),
          file: null,
        },
        creator_department_en: report?.creator_department_en || '',
        download_enable: report?.download_enable === 1,
        watermark_enable: report?.watermark_enable === 1,
      };

      form.initialize(reportFormModel);
      setSubmitSuccess(true);
      setLocationRestrictionEnabled(true);
    },
    // Home首页入口上传报告时，执行这个方法
    initializeWhenRecognizedReport: (file, result) => {
      handleRecognize(file, result);
    },
    updateCreatorInformation: (openId) => {
      if (!!openId) {
        form.batch(() => {
          form.change('creator_name_en', openId);
        });
        updateCreatorInformationDebounce(openId);
      }
    },
  }));

  return (
    <>
      <CardSlotV2 style={style} className={classnames('dbu-report-form', className)} iconList={iconList}>
        {!!file?.file && (
          <div className="dbu-report-form__tips">
            <img src={IMG_AI_TEXT} alt="" />
            <span>{formatMessage({ id: 'dbu_report_form_tips' })}</span>
          </div>
        )}
        <Form layout="fixed" fixedLabelWidth={150}>
          <Form.Item
            required
            label={formatMessage({ id: 'dbu_report_file' })}
            showStatusIcon={showStatusIcon}
            status={formatTeaFormItemStatus(submitted, fileField?.meta)}
          >
            <div className="dbu-report-form__report-file-uploader">
              {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
              <ReportUploader
                recognize={recognizeReportService}
                onRecognized={handleRecognize}
                onRecognitionError={handleRecognitionError}
              />
            </div>
            {!!fileName && (
              <FileNameCard
                style={{ marginTop: 20 }}
                showDelete
                name={fileName}
                onDelete={() => {
                  Modal.confirm({
                    message: formatMessage({ id: 'dbu_common_delete_confirm_title' }),
                    description: formatMessage({ id: 'dbu_common_delete_confirm_desc' }),
                    okText: formatMessage({ id: 'dbh_confirm' }),
                    cancelText: formatMessage({ id: 'dbh_cancel' }),
                    onOk: () => {
                      reset();
                    },
                  });
                }}
              />
            )}
          </Form.Item>
          <Form.Item
            required
            label={formatMessage({ id: 'dbu_report_file_language' })}
            showStatusIcon={showStatusIcon}
            status={formatTeaFormItemStatus(submitted, fileLanguageField?.meta)}
          >
            <ReportRadio
              value={fileLanguageField.input.value || ''}
              onChange={(value) => {
                fileLanguageField.input.onChange(value);
                reportHelperInfo.current.file_cn_language = value;
                reportHelperInfo.current.file_en_language = value;
              }}
            />
          </Form.Item>
          <Form.Item
            required
            label={formatMessage({ id: 'dbu_report_title_cn' })}
            showStatusIcon={showStatusIcon}
            status={formatTeaFormItemStatus(submitted, titleField?.meta)}
          >
            <Input {...titleField.input} placeholder={formatMessage({ id: 'dbu_report_title_placeholder_cn' })} />
          </Form.Item>
          <Form.Item
            required
            label={formatMessage({ id: 'dbu_report_title_en' })}
            showStatusIcon={showStatusIcon}
            status={formatTeaFormItemStatus(submitted, titleENField?.meta)}
          >
            <Input {...titleENField.input} placeholder={formatMessage({ id: 'dbu_report_title_placeholder_en' })} />
          </Form.Item>
          <Form.Item
            required
            label={formatMessage({ id: 'dbu_report_abstract_cn' })}
            showStatusIcon={showStatusIcon}
            status={formatTeaFormItemStatus(submitted, introductionField?.meta)}
          >
            <TextArea
              {...introductionField.input}
              placeholder={formatMessage({ id: 'dbu_report_abstract_placeholder_cn' })}
            />
          </Form.Item>
          <Form.Item
            required
            label={formatMessage({ id: 'dbu_report_abstract_en' })}
            showStatusIcon={showStatusIcon}
            status={formatTeaFormItemStatus(submitted, introductionENField?.meta)}
          >
            <TextArea
              {...introductionENField.input}
              placeholder={formatMessage({ id: 'dbu_report_abstract_placeholder_en' })}
            />
          </Form.Item>
          <Form.Item
            required
            label={formatMessage({ id: 'dbu_report_tag_cn' })}
            showStatusIcon={showStatusIcon}
            status={formatTeaFormItemStatus(submitted, tagField?.meta)}
          >
            <Input {...tagField.input} placeholder={formatMessage({ id: 'dbu_report_tag_placeholder' })} />
          </Form.Item>
          <Form.Item
            required
            label={formatMessage({ id: 'dbu_report_tag_en' })}
            showStatusIcon={showStatusIcon}
            status={formatTeaFormItemStatus(submitted, tagENField?.meta)}
          >
            <Input {...tagENField.input} placeholder={formatMessage({ id: 'dbu_report_tag_placeholder' })} />
          </Form.Item>
          <Form.Item
            required
            label={formatMessage({ id: 'dbu_report_cover' })}
            showStatusIcon={showStatusIcon}
            status={formatTeaFormItemStatus(submitted, coverField?.meta)}
          >
            <ImageUploader
              className={classnames('dbu-report-form__cover-image-uploader', {
                error: submitted && !coverUrl,
              })}
              loading={coverLoading}
              url={coverUrl}
              onUpload={(file, url) => {
                const fileName = file?.name || '';

                form.batch(() => {
                  form.change('cover', {
                    key: `${DBI_IMG_COS_PATH}/${Date.now()}_cover-${fileName}`,
                    url,
                    file_name: fileName,
                    file,
                  });
                });

                onUploadCover?.(file);
              }}
              onImageLoadError={() => {
                form.change('cover', null);
              }}
            />
          </Form.Item>
          <Form.Item
            required
            label={formatMessage({ id: 'dbu_report_location' })}
            showStatusIcon={showStatusIcon}
            status={formatTeaFormItemStatus(submitted, locationField?.meta)}
          >
            <LocationSelect
              {...locationField.input}
              options={locationOptionsWithDisabled}
              value={locationField.input.value || []}
            />
          </Form.Item>
          <Form.Item
            label={formatMessage({ id: 'dbu_report_permission' })}
            showStatusIcon={showStatusIcon}
            status={formatTeaFormItemStatus(submitted, permissionField?.meta)}
          >
            <PermissionSelect
              options={permissionOptions}
              value={Array.isArray(permissionField.input.value) ? permissionField.input.value : []}
              onChange={(nextValue) => {
                permissionField.input.onChange(Array.isArray(nextValue) ? nextValue : []);
              }}
              onBlur={permissionField.input.onBlur}
            />
          </Form.Item>
          <Form.Item label={formatMessage({ id: 'dbu_report_download_enable' })}>
            <Switch {...downloadEnableField.input} />
          </Form.Item>
          <Form.Item label={formatMessage({ id: 'dbu_report_watermark_enable' })}>
            <Switch {...watermarkEnableField.input} />
          </Form.Item>
        </Form>
        <Form.Action>
          <Button
            type="primary"
            loading={submitting}
            disabled={!formAvailable}
            onClick={(e) => {
              e?.stopPropagation();
              handleSubmitInPage();
            }}
          >
            {formatMessage({ id: 'dbu_common_save' })}
          </Button>
          <Button
            onClick={(e) => {
              e?.stopPropagation();
              onBack?.();
              history.goBack();
            }}
          >
            {formatMessage({ id: 'dbu_common_back' })}
          </Button>
          <Button
            loading={previewLoading}
            disabled={!formValid}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={async (e) => {
              e?.stopPropagation();
              try {
                setPreviewLoading(true);
                let valuesNew = { ...values };
                const fileNew = await uploadMarketReportFile(valuesNew);
                valuesNew = { ...valuesNew, file: fileNew, file_en: null };

                form.batch(() => {
                  form.change('file', fileNew);
                  form.change('file_en', null);
                });

                setPreviewVisible(true);
                setPreviewReport(
                  buildMarketReport(valuesNew, { permissionOptions, reportHelperInfo: reportHelperInfo.current }),
                );
              } catch (error) {
                console.error(error);
              } finally {
                setPreviewLoading(false);
                onPreview?.();
              }
            }}
          >
            {formatMessage({ id: 'dbu_common_preview' })}
          </Button>
          <Button
            loading={reportPublishLoading}
            disabled={!submitSuccess}
            onClick={(e) => {
              e?.stopPropagation();
              void handlePublishAfterValidate();
            }}
          >
            {formatMessage({ id: 'dbu_common_publish' })}
          </Button>
        </Form.Action>
      </CardSlotV2>
      <ReportPreviewModal
        visible={previewVisible}
        disabledShare={!submitSuccess}
        report={previewReport}
        onClose={() => {
          setPreviewVisible(false);
          setPreviewReport(null);
        }}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onShare={async () => {
          const isPublished = await handlePublishAfterValidate();
          if (!isPublished) {
            return;
          }

          setPreviewVisible(false);
          setPreviewReport(null);
        }}
      />
    </>
  );
};

// @ts-ignore
export default React.forwardRef(MarketReportForm);
